import { Direction } from '@modules/common/types';
import {
  removeContentFromMetadata,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import {
  useAddTextWidget,
  useIsDashboardEditable,
  useUpsertDashboard,
} from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { RemoveCircleRounded } from '@mui/icons-material';
import ControlPointDuplicateRoundedIcon from '@mui/icons-material/ControlPointDuplicateRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  BoxProps,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { DashboardCell } from 'generated/graphql';
import { useState } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';

interface TextWidgetActionsProps {
  className?: string;
  sx?: BoxProps['sx'];
  cell: DashboardCell;
  isEditorActive: boolean;
  rowID: string;
  textContent: string;
}

export const TextWidgetActions = ({
  sx,
  cell,
  rowID,
  className,
  textContent,
  isEditorActive,
}: TextWidgetActionsProps) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const metadata = useDashboardStore(({ metadata }) => metadata);
  const { upsertDashboard, loading: isRemoveLoading } = useUpsertDashboard();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);

  const addTextWidget = useAddTextWidget({
    rowID,
    cellInsertDirection: Direction.Right,
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const isEditable = useIsDashboardEditable();

  const removeWidget = async () => {
    if (!dashboard || !isEditable) {
      return;
    }

    const metadataClone = removeContentFromMetadata({
      metadata,
      contentID: cell.contentID,
    });

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: metadataClone,
    });

    await upsertDashboard({
      dashboardProps: {
        metadataV2: metadataClone,
        deleteWidget: {
          id: cell.contentID,
        },
      },
      shouldRefetchDashboard: true,
    });

    trackDashboardEvent({
      eventName: 'Dashboard Remove Content',
      type: 'text',
      from: 'Dashboard Page',
    });

    handleMenuClose();
  };

  const duplicateWidget = async () => {
    if (!dashboard || !isEditable) {
      return;
    }

    handleMenuClose();
    const newCell = await addTextWidget(textContent);
    const cellElement = document.getElementById(newCell.id);

    if (cellElement) {
      scrollIntoView(cellElement, {
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  };

  if (!isEditable) {
    return null;
  }

  const isMenuOpen = Boolean(menuAnchor);

  return (
    <Box
      className={className}
      sx={{
        ...sx,
        visibility: isEditorActive || isMenuOpen ? 'visible' : 'hidden',
      }}
    >
      <IconButton size="small" onClick={handleMenuClick}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={duplicateWidget}>
          <ListItemIcon>
            <ControlPointDuplicateRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={removeWidget} disabled={isRemoveLoading}>
          <ListItemIcon>
            <RemoveCircleRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};
